import { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, Plus, Trash2, X, Pencil } from "lucide-react";
import ListToolbar from "@/components/ListToolbar";

const EMPTY_FORM = { name: "", description: "", base_price: "", currency: "USD", category: "" };

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cotflow_view_products") || "card");
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => api.get("/products").then(r => setProducts(r.data)).catch(() => toast.error("Error")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      base_price: (p.base_price ?? "").toString(),
      currency: p.currency || "USD",
      category: p.category || "",
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, base_price: parseFloat(form.base_price) || 0 };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (editingId ? "Error al actualizar" : "Error al crear"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Eliminado");
    load();
  };

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase()) || (p.category || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black font-['Satoshi'] tracking-tight text-slate-900">Productos y/o Servicios</h1>
          <p className="text-slate-500 mt-1">{products.length} registrados</p>
        </div>
        <button onClick={openCreate} className="rounded-lg bg-slate-900 text-white hover:bg-slate-800 font-medium px-5 py-2.5 text-sm transition-all active:scale-95 flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={18} /> Nuevo
        </button>
      </div>

      <ListToolbar search={search} onSearch={setSearch} viewMode={viewMode} onViewMode={m => { setViewMode(m); localStorage.setItem("cotflow_view_products", m); }} />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Package size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{search ? "Sin resultados" : "Sin productos"}</p>
        </div>
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all" data-testid={`product-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-slate-900">{p.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                  {p.category && <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{p.category}</span>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`edit-${p.id}`} title="Editar"><Pencil size={16} /></button>
                  <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 font-bold text-blue-600">{p.currency} ${p.base_price?.toLocaleString()}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Nombre</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Descripción</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Categoría</th>
              <th className="text-right px-5 py-3 font-semibold text-slate-600">Precio</th>
              <th className="px-5 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-slate-50/50" data-testid={`product-row-${p.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{p.name}</td>
                  <td className="px-5 py-3 text-slate-500 max-w-xs truncate">{p.description}</td>
                  <td className="px-5 py-3 text-slate-500">{p.category || "-"}</td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">{p.currency} ${p.base_price?.toLocaleString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`edit-row-${p.id}`} title="Editar"><Pencil size={16} /></button>
                      <button onClick={() => handleDelete(p.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold font-['Satoshi']">{editingId ? "Editar Producto/Servicio" : "Nuevo Producto/Servicio"}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Nombre *</label>
                <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="product-form-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Descripción</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-testid="product-form-desc" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Precio base</label>
                  <input type="number" value={form.base_price} onChange={e => setForm({...form, base_price: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="product-form-price" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Moneda</label>
                  <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm"><option>USD</option><option>CLP</option><option>UF</option><option>EUR</option></select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Categoría</label>
                  <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Ej: RPA" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <button type="submit" className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-all active:scale-[0.98]" data-testid="product-form-submit">
                {editingId ? "Guardar cambios" : "Crear"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
